import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  Input,
  NgZone,
  OnDestroy,
  Output,
  ViewChild,
} from '@angular/core';
import { FestiveService } from '../../../../services/festive.service';
import { FestiveDoodleComponent } from '../festive-doodle/festive-doodle.component';

interface Rocket {
  x: number;
  y: number;
  vy: number;
  targetY: number;
  color: string;
}

interface Spark {
  x: number;
  y: number;
  // Previous position, so each spark draws as a short streak
  px: number;
  py: number;
  vx: number;
  vy: number;
  life: number;
  maxLife: number;
  color: string;
  size: number;
  glitter: boolean;
  gravity: number;
}

interface Flash {
  x: number;
  y: number;
  life: number;
  color: string;
}

const PALETTE = ['#FFD166', '#F59E0B', '#FF7A1A', '#F472B6', '#E11D74', '#FFF6D5', '#34D399', '#60A5FA'];
const FOUNTAIN_COLORS = ['#FFF6D5', '#FFD166', '#FFD166', '#F59E0B', '#FF9F1C'];
const GRAVITY = 160; // px/s² for burst sparks
const ROCKET_GRAVITY = GRAVITY * 2.2;
const FOUNTAIN_GRAVITY = 520;
const FLASH_LIFE = 0.28;
const MAX_DPR = 2;

/** '#RRGGBB' → the same colour at zero alpha */
function transparent(hex: string): string {
  const n = parseInt(hex.slice(1), 16);
  return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, 0)`;
}

/**
 * Canvas fireworks for the festival skin: rockets that burst into streaking sparks, and optional anar
 * fountains rising from the bottom edge. Never takes pointer events or focus, so billing and barcode
 * scanning keep working underneath. Shows nothing outside a festival or with reduced motion.
 */
@Component({
  selector: 'app-festive-fireworks',
  standalone: true,
  imports: [CommonModule, FestiveDoodleComponent],
  templateUrl: './festive-fireworks.component.html',
  styleUrls: ['./festive-fireworks.component.scss'],
})
export class FestiveFireworksComponent implements AfterViewInit, OnDestroy {
  /** Rocket run time in ms; launching stops a little before so the last bursts can fade */
  @Input() duration = 3500;
  /** 'show' is a gentle login display, 'celebrate' a denser burst with a dimmed backdrop */
  @Input() intensity: 'show' | 'celebrate' = 'show';
  /** Fill the nearest positioned parent instead of the viewport */
  @Input() @HostBinding('class.contained') contained = false;
  /** Anar fountains along the bottom edge, as x positions from 0 to 1 */
  @Input() fountains: number[] = [];
  /** How long the fountains burn (ms) before dying down */
  @Input() fountainDuration = 9000;
  /** Optional greeting card shown in the middle while it plays */
  @Input() greeting = '';
  @Input() subtitle = '';
  @Output() finished = new EventEmitter<void>();

  @ViewChild('canvas') canvasRef?: ElementRef<HTMLCanvasElement>;

  readonly active: boolean;
  done = false;

  private ctx?: CanvasRenderingContext2D;
  private frame = 0;
  private rockets: Rocket[] = [];
  private sparks: Spark[] = [];
  private flashes: Flash[] = [];
  private width = 0;
  private height = 0;
  private scale = 1;
  private startedAt = 0;
  private lastFrameAt = 0;
  private nextLaunchAt = 0;
  private fountainCarry = 0;
  // Watches the host, not the window: a contained canvas can be laid out after first render
  private resizeObserver?: ResizeObserver;

  constructor(
    festiveService: FestiveService,
    private host: ElementRef<HTMLElement>,
    private zone: NgZone,
  ) {
    this.active = !!festiveService.festival && !festiveService.prefersReducedMotion();
  }

  get celebrate(): boolean {
    return this.intensity === 'celebrate';
  }

  ngAfterViewInit(): void {
    const canvas = this.canvasRef?.nativeElement;
    const ctx = canvas?.getContext('2d');
    if (!this.active || !canvas || !ctx) return;
    this.ctx = ctx;
    this.resize();
    this.resizeObserver = new ResizeObserver(() => this.resize());
    this.resizeObserver.observe(this.host.nativeElement);

    // Animation frames stay out of Angular so the POS doesn't re-render 60 times a second
    this.zone.runOutsideAngular(() => {
      this.frame = requestAnimationFrame((t) => this.tick(t));
    });
  }

  ngOnDestroy(): void {
    cancelAnimationFrame(this.frame);
    this.resizeObserver?.disconnect();
  }

  private tick(now: number): void {
    const ctx = this.ctx!;
    // Hold the show until the host has been laid out
    if (!this.startedAt) {
      if (this.height < 40 || this.width < 40) {
        this.frame = requestAnimationFrame((t) => this.tick(t));
        return;
      }
      this.startedAt = this.lastFrameAt = now;
      const salvo = this.celebrate ? [0.25, 0.5, 0.75] : [0.3, 0.7];
      salvo.forEach((x) => this.launch(x));
      this.nextLaunchAt = now + 500;
    }
    const dt = Math.min((now - this.lastFrameAt) / 1000, 0.05);
    this.lastFrameAt = now;
    const elapsed = now - this.startedAt;
    const launching = elapsed < this.duration - 1300;
    const fountainPower = this.fountainPower(elapsed);

    if (launching && now >= this.nextLaunchAt) {
      this.launch();
      const gap = this.celebrate ? 230 : 480;
      this.nextLaunchAt = now + gap + Math.random() * gap;
    }
    if (fountainPower > 0) this.feedFountains(dt, fountainPower);

    this.step(dt);

    ctx.clearRect(0, 0, this.width, this.height);
    ctx.globalCompositeOperation = 'lighter';
    this.draw(ctx, fountainPower);
    ctx.globalCompositeOperation = 'source-over';

    if (!launching && fountainPower <= 0 && !this.rockets.length && !this.sparks.length && !this.flashes.length) {
      this.finish();
      return;
    }
    this.frame = requestAnimationFrame((t) => this.tick(t));
  }

  /** 0..1: full strength, then a slow die-down over the last third like a real anar */
  private fountainPower(elapsed: number): number {
    if (!this.fountains.length || elapsed >= this.fountainDuration) return 0;
    const fadeFrom = this.fountainDuration * 0.65;
    const rampIn = Math.min(elapsed / 400, 1);
    return elapsed < fadeFrom ? rampIn : rampIn * (1 - (elapsed - fadeFrom) / (this.fountainDuration - fadeFrom));
  }

  private launch(xRatio = 0.12 + Math.random() * 0.76): void {
    const targetY = this.height * (0.1 + Math.random() * 0.3);
    const distance = this.height - targetY;
    this.rockets.push({
      x: this.width * xRatio,
      y: this.height,
      // Launch speed that just reaches the target under gravity
      vy: -Math.sqrt(2 * ROCKET_GRAVITY * distance),
      targetY,
      color: PALETTE[Math.floor(Math.random() * PALETTE.length)],
    });
  }

  private explode(rocket: Rocket): void {
    const willow = Math.random() < 0.25; // gold, long drooping trails
    const ring = !willow && Math.random() < 0.3;
    const count = Math.round((ring ? 70 : 120 + Math.random() * 40) * (this.celebrate ? 1 : 0.85));
    const color = willow ? '#FFD166' : rocket.color;
    const second = willow ? '#F59E0B' : PALETTE[Math.floor(Math.random() * PALETTE.length)];
    const power = (190 + Math.random() * 120) * this.scale;

    this.flashes.push({ x: rocket.x, y: rocket.y, life: FLASH_LIFE, color });
    for (let i = 0; i < count; i++) {
      const angle = (i / count) * Math.PI * 2 + Math.random() * 0.15;
      const speed = ring ? power : power * Math.sqrt(0.15 + Math.random() * 0.85);
      const maxLife = willow ? 1.8 + Math.random() * 0.8 : 1.1 + Math.random() * 0.7;
      this.sparks.push({
        x: rocket.x,
        y: rocket.y,
        px: rocket.x,
        py: rocket.y,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed,
        life: maxLife,
        maxLife,
        color: i % 3 === 0 ? second : color,
        size: (1.3 + Math.random() * 1.1) * this.scale,
        glitter: willow || Math.random() < 0.3,
        gravity: willow ? GRAVITY * 0.7 : GRAVITY,
      });
    }
  }

  /** Each fountain throws a steady cone of gold sparks up from the bottom edge */
  private feedFountains(dt: number, power: number): void {
    const perSecond = 150 * power;
    this.fountainCarry += perSecond * dt;
    const count = Math.floor(this.fountainCarry);
    this.fountainCarry -= count;
    const baseY = this.height - 4;

    for (const ratio of this.fountains) {
      const x = this.width * ratio;
      for (let i = 0; i < count; i++) {
        const angle = -Math.PI / 2 + (Math.random() - 0.5) * 0.75;
        const speed = (300 + Math.random() * 170) * this.scale * (0.55 + 0.45 * power);
        const maxLife = 0.6 + Math.random() * 0.6;
        this.sparks.push({
          x: x + (Math.random() - 0.5) * 6,
          y: baseY,
          px: x,
          py: baseY,
          vx: Math.cos(angle) * speed,
          vy: Math.sin(angle) * speed,
          life: maxLife,
          maxLife,
          color: FOUNTAIN_COLORS[Math.floor(Math.random() * FOUNTAIN_COLORS.length)],
          size: (0.9 + Math.random() * 0.9) * this.scale,
          glitter: Math.random() < 0.5,
          gravity: FOUNTAIN_GRAVITY,
        });
      }
    }
  }

  private step(dt: number): void {
    const drag = Math.pow(0.5, dt);

    for (let i = this.rockets.length - 1; i >= 0; i--) {
      const r = this.rockets[i];
      r.vy += ROCKET_GRAVITY * dt;
      r.y += r.vy * dt;
      // Ember trail behind the rocket
      for (let k = 0; k < 2; k++) {
        this.sparks.push({
          x: r.x + (Math.random() - 0.5) * 2,
          y: r.y + 3,
          px: r.x,
          py: r.y + 3,
          vx: (Math.random() - 0.5) * 24,
          vy: 20 + Math.random() * 40,
          life: 0.45,
          maxLife: 0.45,
          color: '#FFD166',
          size: 1.2 * this.scale,
          glitter: true,
          gravity: GRAVITY,
        });
      }
      if (r.y <= r.targetY || r.vy >= 0) {
        this.explode(r);
        this.rockets.splice(i, 1);
      }
    }

    for (let i = this.sparks.length - 1; i >= 0; i--) {
      const s = this.sparks[i];
      s.px = s.x;
      s.py = s.y;
      s.vx *= drag;
      s.vy = s.vy * drag + s.gravity * dt;
      s.x += s.vx * dt;
      s.y += s.vy * dt;
      s.life -= dt;
      if (s.life <= 0) this.sparks.splice(i, 1);
    }

    for (let i = this.flashes.length - 1; i >= 0; i--) {
      this.flashes[i].life -= dt;
      if (this.flashes[i].life <= 0) this.flashes.splice(i, 1);
    }
  }

  private draw(ctx: CanvasRenderingContext2D, fountainPower: number): void {
    // Warm light where each fountain burns
    if (fountainPower > 0) {
      for (const ratio of this.fountains) {
        const x = this.width * ratio;
        const radius = 90 * this.scale * (0.85 + Math.random() * 0.15);
        this.glow(ctx, x, this.height, radius, '#FFB020', 0.55 * fountainPower);
      }
    }

    // Burst flashes
    for (const f of this.flashes) {
      const t = f.life / FLASH_LIFE;
      this.glow(ctx, f.x, f.y, 120 * this.scale * (1.2 - t * 0.4), f.color, 0.45 * t);
    }

    ctx.lineCap = 'round';
    for (const s of this.sparks) {
      const fade = s.life / s.maxLife;
      const alpha = s.glitter ? fade * (0.35 + Math.random() * 0.65) : fade;
      ctx.globalAlpha = alpha;
      ctx.strokeStyle = s.color;
      ctx.lineWidth = s.size * (0.6 + 0.4 * fade);
      ctx.beginPath();
      ctx.moveTo(s.px, s.py);
      ctx.lineTo(s.x, s.y);
      ctx.stroke();
    }

    for (const r of this.rockets) {
      ctx.globalAlpha = 1;
      this.glow(ctx, r.x, r.y, 14 * this.scale, '#FFD166', 0.8);
    }
    ctx.globalAlpha = 1;
  }

  private glow(ctx: CanvasRenderingContext2D, x: number, y: number, radius: number, color: string, alpha: number): void {
    const gradient = ctx.createRadialGradient(x, y, 0, x, y, radius);
    gradient.addColorStop(0, color);
    // Fade to the same colour, not transparent black, or the halo shows a grey ring on light pages
    gradient.addColorStop(1, transparent(color));
    ctx.globalAlpha = Math.max(0, Math.min(alpha, 1));
    ctx.fillStyle = gradient;
    ctx.beginPath();
    ctx.arc(x, y, radius, 0, Math.PI * 2);
    ctx.fill();
    ctx.globalAlpha = 1;
  }

  private resize(): void {
    const canvas = this.canvasRef?.nativeElement;
    if (!canvas || !this.ctx) return;
    const rect = this.host.nativeElement.getBoundingClientRect();
    const dpr = Math.min(window.devicePixelRatio || 1, MAX_DPR);
    this.width = rect.width;
    this.height = rect.height;
    this.scale = Math.min(Math.max(Math.min(rect.width, rect.height) / 800, 0.6), 1.4);
    canvas.width = Math.round(rect.width * dpr);
    canvas.height = Math.round(rect.height * dpr);
    this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  }

  private finish(): void {
    this.resizeObserver?.disconnect();
    this.zone.run(() => {
      this.done = true;
      this.finished.emit();
    });
  }
}
